import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';

import api from 'service/index';
import { br } from 'utils/i18nUtils';
import type { RootState } from 'App/Store';
import { QueryReturnValue } from 'types/Request';

import { NotificationType } from 'modules/notifications/types/Notification';
import type { GetLatestMessageRequest } from 'modules/kiosk/types/GetLatestMessageRequest';
import type { GetLatestMessageResponse } from 'modules/kiosk/types/GetLatestMessageResponse';

import {
  ADMIN, KIOSK, ROOT, SELF_SERVICE, SLOT,
} from 'constants/url';

import { addNotification } from 'modules/notifications/actions';

import { getAdminToken, getToken } from 'modules/auth/selectors';

import { Kiosk } from './types/Kiosk';
import { ApiErrorResponse } from '../../types/Error';
import { GetKioskDataRequest } from './types/GetKioskData';
import { CloseKeysSafeRequest, OpenKeysSafeRequest, OpenKeysSafeResponse } from './types/KeysSafe';
import { GetReadySelfServicesRequest, GetReadySelfServicesResponse } from './types/GetReadySelfServices';

const kioskApi = api.injectEndpoints({
  endpoints: (build) => ({
    getKioskData: build.query<Kiosk, GetKioskDataRequest>({
      query: ({ kioskId }) => ({
        method: 'GET',
        url: `admin${KIOSK}/${kioskId}`,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            addNotification({
              title: {
                id: 'admin.errors.kioskError',
                defaultMessage: 'An error occurred while getting kiosk data',
              },
              description: {
                id: 'admin.errors.retry',
                defaultMessage: 'Please try again. If the problem persists, contact the support team.',
              },
              collapse: {
                title: {
                  id: 'admin.errors.supportContact',
                  defaultMessage: 'Support contact information',
                },
                description: {
                  id: 'admin.errors.support',
                  defaultMessage: 'Mo-Fr from 9am to 5pm. (CET){br}support@fleetback.com{br}+32 2 80 80 828',
                  values: { br },
                },
              },
              type: NotificationType.ERROR,
            }),
          );
        }
      },
    }),
    openSlot: build.query<OpenKeysSafeResponse, OpenKeysSafeRequest>({
      query: ({ kioskId, selfServiceId, reopen }) => ({
        method: 'POST',
        body: {
          processId: selfServiceId,
        },
        url: `${KIOSK}/${kioskId}${SELF_SERVICE}/${selfServiceId}/slot/${reopen ? 'reopen' : 'open'}`,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            addNotification({
              title: {
                id: 'key.error.opening',
                defaultMessage: 'An error occurred while opening the slot',
              },
              description: {
                id: 'defaultError.description',
                defaultMessage: 'Please try again later or contact us.',
              },
              type: NotificationType.ERROR,
            }),
          );
        }
      },
      transformResponse: (response, __, { selfServiceId }) => ({
        ...(response as OpenKeysSafeResponse),
        selfServiceId,
      }),
    }),
    openSlotAsAdmin: build.query<OpenKeysSafeRequest, OpenKeysSafeRequest>({
      query: ({
        kioskId, slotId, selfServiceId, emergency,
      }) => ({
        method: 'POST',
        url: `${emergency ? ROOT : ADMIN}${KIOSK}/${kioskId}/slots/${slotId}/open`,
        body: { selfServiceId },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            addNotification({
              title: {
                id: 'key.error.opening',
                defaultMessage: 'An error occurred while opening the slot',
              },
              description: {
                id: 'defaultError.description',
                defaultMessage: 'Please try again later or contact us.',
              },
              type: NotificationType.ERROR,
            }),
          );
        }
      },
      transformResponse(_, __, request) {
        return request;
      },
    }),
    /** @description Reset the kiosk. Should be used only on Partteam. */
    reset: build.mutation<OpenKeysSafeResponse, OpenKeysSafeRequest>({
      queryFn({
        kioskId, selfServiceId, token, doorTimeout,
      }, { getState }, __, baseQuery) {
        const authToken = token ?? getToken(getState() as RootState);
        return baseQuery({
          method: 'POST',
          url: `${KIOSK}/${kioskId}${SELF_SERVICE}/${selfServiceId}/reset`,
          params: { doorTimeout },
          headers: { Authorization: authToken },
        }) as QueryReturnValue<OpenKeysSafeResponse, ApiErrorResponse, FetchBaseQueryMeta>;
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            addNotification({
              title: {
                id: 'key.error.closing',
                defaultMessage: 'An error occurred while closing the slot',
              },
              description: {
                id: 'defaultError.description',
                defaultMessage: 'Please try again later or contact us.',
              },
              type: NotificationType.ERROR,
            }),
          );
        }
      },
    }),
    closeSlotAsAdmin: build.query<CloseKeysSafeRequest, CloseKeysSafeRequest>({
      query: ({
        kioskId, slotId, selfServiceId, emergency,
      }) => ({
        method: 'POST',
        url: `${emergency ? ROOT : ADMIN}${KIOSK}/${kioskId}/slots/${slotId}/close`,
        body: { selfServiceId },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            addNotification({
              title: {
                id: 'key.error.closing',
                defaultMessage: 'An error occurred while closing the slot',
              },
              description: {
                id: 'defaultError.description',
                defaultMessage: 'Please try again later or contact us.',
              },
              type: NotificationType.ERROR,
            }),
          );
        }
      },
      transformResponse(_, __, request) {
        return request;
      },
    }),
    closeAllSlotsAsAdmin: build.mutation<undefined, { kioskId: string; adminToken?: string }>({
      queryFn({ kioskId, adminToken }, { getState }, __, baseQuery) {
        const authToken = adminToken ?? getAdminToken(getState() as RootState);
        return baseQuery({
          method: 'POST',
          url: `${ADMIN}${KIOSK}/${kioskId}/reset`,
          headers: { Authorization: authToken },
        }) as QueryReturnValue<undefined, ApiErrorResponse, FetchBaseQueryMeta>;
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            addNotification({
              title: {
                id: 'key.error.closing',
                defaultMessage: 'An error occurred while closing the slot',
              },
              description: {
                id: 'defaultError.description',
                defaultMessage: 'Please try again later or contact us.',
              },
              type: NotificationType.ERROR,
            }),
          );
        }
      },
    }),
    closeSlot: build.mutation<undefined, CloseKeysSafeRequest>({
      query: ({ selfServiceId, kioskId }) => ({
        method: 'POST',
        url: `${KIOSK}/${kioskId}${SELF_SERVICE}/${selfServiceId}/slot/close`,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            addNotification({
              title: {
                id: 'key.error.closing',
                defaultMessage: 'An error occurred while closing the slot',
              },
              description: {
                id: 'defaultError.description',
                defaultMessage: 'Please try again later or contact us.',
              },
              type: NotificationType.ERROR,
            }),
          );
        }
      },
    }),
    updateSlot: build.mutation<undefined, CloseKeysSafeRequest>({
      query: ({ selfServiceId, kioskId, ...body }) => ({
        method: 'PATCH',
        url: `${KIOSK}/${kioskId}${SELF_SERVICE}/${selfServiceId}/keys`,
        body,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            addNotification({
              title: {
                id: 'key.error.closing',
                defaultMessage: 'An error occurred while closing the slot',
              },
              description: {
                id: 'defaultError.description',
                defaultMessage: 'Please try again later or contact us.',
              },
              type: NotificationType.ERROR,
            }),
          );
        }
      },
    }),
    updateSlotAsAdmin: build.mutation<undefined, CloseKeysSafeRequest>({
      query: ({ slotId, kioskId, ...body }) => ({
        method: 'PATCH',
        url: `${ADMIN}${KIOSK}/${kioskId}/slots/${slotId}/keys`,
        body,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            addNotification({
              title: {
                id: 'key.error.closing',
                defaultMessage: 'An error occurred while closing the slot',
              },
              description: {
                id: 'defaultError.description',
                defaultMessage: 'Please try again later or contact us.',
              },
              type: NotificationType.ERROR,
            }),
          );
        }
      },
    }),
    unprepareSlotAsAdmin: build.mutation<undefined, CloseKeysSafeRequest>({
      query: ({ selfServiceId, kioskId, slotId }) => ({
        method: 'POST',
        url: `${ADMIN}${KIOSK}/${kioskId}/slots/${slotId}/unprepare`,
        body: { selfServiceId },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            addNotification({
              title: {
                id: 'key.error.unprepare',
                defaultMessage: 'An error occurred while retrieving the keys',
              },
              description: {
                id: 'defaultError.description',
                defaultMessage: 'Please try again later or contact us.',
              },
              type: NotificationType.ERROR,
            }),
          );
        }
      },
    }),
    getReadySelfServices: build.query<GetReadySelfServicesResponse, GetReadySelfServicesRequest>({
      query: ({ kioskId, ...params }) => ({
        method: 'GET',
        url: `${ADMIN}${KIOSK}/${kioskId}${SELF_SERVICE}/ready`,
        params,
      }),
      transformResponse: (response: GetReadySelfServicesResponse, _, args) => response.map((selfService) => ({
        ...selfService,
        type: args.type,
      })),
    }),
    openDoorsEmergency: build.query<undefined, { kioskId: string }>({
      query: ({ kioskId }) => ({
        method: 'POST',
        url: `${ADMIN}${KIOSK}/${kioskId}/emergencyOpen`,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            addNotification({
              title: {
                id: 'key.error.opening',
                defaultMessage: 'An error occurred while opening the slot',
              },
              description: {
                id: 'defaultError.description',
                defaultMessage: 'Please try again later or contact us.',
              },
              type: NotificationType.ERROR,
            }),
          );
        }
      },
    }),
    getLatestMessage: build.query<GetLatestMessageResponse, GetLatestMessageRequest>({
      query: ({ kioskId, slotId, processId }) => ({
        method: 'GET',
        url: `${KIOSK}/${kioskId}${SLOT}/${slotId}/process/${processId}/latestMessage`,
      }),
    }),
    getKiosk: build.query<Kiosk, { kioskId: string }>({
      query: ({ kioskId }) => ({
        method: 'GET',
        url: `${KIOSK}/${kioskId}`,
      }),
    }),
  }),
});

export const {
  useGetKioskDataQuery, useGetReadySelfServicesQuery, useLazyGetLatestMessageQuery, useGetKioskQuery,
} = kioskApi;

export default kioskApi;
